import { queryClient } from '../../../api/query-client'
import {
  DeltakerMutationOptions,
  DeltakerTypes,
  IDeltaker,
  IDeltakerMutateVars,
  IDeltakerVars,
} from '../types'
import {
  getDeltakerKeys,
  getPreviousDeltakere,
  isDeltakerEqual,
} from './helpers'
import { deltakerApi } from './deltaker-api'
import store from '../../../reducers/store'
import { dangerToast } from '../../../common/toast'
import {
  DependencyNotPersisted,
  getIdFromSelfUrl,
  shouldIgnoreError,
} from '../../../common/query'
import { ENDPOINT } from '../../../constants'
import { onlineManager } from '@tanstack/react-query'
import { kvitteringQueryHelpers } from '../../kvitteringer/queries/helpers'
import { AxiosResponse } from 'axios'
import { offlineLexicon } from '../../offline-lexicon'

/**
 * Optimistically updates deltakere
 *
 * @returns Previous deltakere (Context that
 * is passed to onError).
 */
const onMutate: (
  updateDeltakere: (deltakere: IDeltaker[], deltaker: IDeltaker) => IDeltaker[]
) => DeltakerMutationOptions['onMutate'] =
  (updateDeltakere) =>
  async ({ kvitteringId, type, deltaker }) => {
    await kvitteringQueryHelpers.cancelKvitteringQuery(kvitteringId)

    const previousDeltakere = getPreviousDeltakere(
      type,
      kvitteringQueryHelpers.getKvitteringCache(kvitteringId)
    )

    kvitteringQueryHelpers.updateKvitteringCache(kvitteringId, (draft) => {
      switch (type) {
        case DeltakerTypes.VIRKSOMHET:
          draft.virksomhetsDeltagere = updateDeltakere(
            draft.virksomhetsDeltagere ?? [],
            deltaker
          )
          break
        case DeltakerTypes.MATTILSYNET:
          draft.mattilsynetDeltagere = updateDeltakere(
            draft.mattilsynetDeltagere ?? [],
            deltaker
          )
          break
        case DeltakerTypes.ANDRE:
          draft.andreDeltagere = updateDeltakere(
            draft.andreDeltagere ?? [],
            deltaker
          )
          break
      }
    })

    return { previousDeltakere }
  }

/** Displays error toast and reverts cache to previous deltakere. */
const onError: DeltakerMutationOptions['onError'] = (
  error,
  { kvitteringId, type },
  context
) => {
  if (shouldIgnoreError(error)) {
    return
  }

  store.dispatch(
    dangerToast(`Det har skjedd en feil med ${type}. Kunne ikke lagre.`)
  )

  kvitteringQueryHelpers.updateKvitteringCache(kvitteringId, (kvittering) => {
    switch (type) {
      case DeltakerTypes.VIRKSOMHET:
        return {
          ...kvittering,
          virksomhetsDeltagere:
            context?.previousDeltakere ?? kvittering.virksomhetsDeltagere,
        }
      case DeltakerTypes.MATTILSYNET:
        return {
          ...kvittering,
          mattilsynetDeltagere:
            context?.previousDeltakere ?? kvittering.mattilsynetDeltagere,
        }
      case DeltakerTypes.ANDRE:
        return {
          ...kvittering,
          andreDeltagere:
            context?.previousDeltakere ?? kvittering.andreDeltagere,
        }
    }
  })
}

const getMutationUrl = (
  kvitteringId: string,
  { type, deltaker }: IDeltakerVars
) => {
  let mutationUrl = `${ENDPOINT.TILSYNSKVITTERING}/${type}/${deltaker.id}`
  let persistedId = deltaker.id.toString()
  if (typeof deltaker.id !== 'number') {
    const localDeltaker = offlineLexicon.get(
      offlineLexicon.types.deltaker,
      deltaker.id ?? '',
      kvitteringId
    )
    mutationUrl = localDeltaker?._links?.self.href ?? mutationUrl
    persistedId = localDeltaker?.id?.toString() ?? ''
  }

  if (!mutationUrl) {
    throw new DependencyNotPersisted()
  }

  return [mutationUrl, persistedId] as const
}

/** Gets and merges the mutation url with the variables. */
const withMutationUrl =
  (request: (variables: IDeltakerMutateVars) => Promise<AxiosResponse>) =>
  (variables: IDeltakerMutateVars) => {
    const { kvitteringId, type, deltaker } = variables

    let url = ''
    let pId: string | undefined = ''
    try {
      const [mutationUrl, persistedId] = getMutationUrl(kvitteringId, {
        type,
        deltaker,
      })
      url = mutationUrl
      pId = persistedId ?? deltaker.id
    } catch (error) {
      return Promise.reject(error)
    }

    return request({
      ...variables,
      deltaker: {
        ...deltaker,
        id: pId,
        _links: {
          ...deltaker._links,
          self: {
            href: url,
          },
        },
      },
      type,
    })
  }

const setDeltakerDefaults = (entity: string) => {
  // Add deltaker
  queryClient.setMutationDefaults(getDeltakerKeys(entity).addBase(), {
    mutationFn: deltakerApi.post,
    onMutate: onMutate((deltakere, deltaker) => {
      if (onlineManager.isOnline()) {
        return deltakere
      }

      return [...deltakere, deltaker]
    }),
    onSuccess: (res, { kvitteringId, deltaker, type }) => {
      const persistedId =
        getIdFromSelfUrl(res.headers.location)?.toString() ?? deltaker.id
      const newDeltaker = {
        ...deltaker,
        id: persistedId,
        _links: {
          self: {
            href: `${ENDPOINT.TILSYNSKVITTERING}/${type}/${persistedId}`,
          },
        },
      } satisfies IDeltaker

      offlineLexicon.set(
        offlineLexicon.types.deltaker,
        deltaker.id + '',
        kvitteringId,
        newDeltaker
      )
    },
    onError,
    onSettled: (_, __, { kvitteringId }) => {
      if (queryClient.isMutating() > 1) {
        return
      }

      return kvitteringQueryHelpers.invalidateKvittering(kvitteringId)
    },
  } satisfies DeltakerMutationOptions)

  // Update deltaker
  queryClient.setMutationDefaults(getDeltakerKeys(entity).updateBase(), {
    mutationFn: withMutationUrl((variables) => deltakerApi.put(variables)),
    onMutate: onMutate((deltakere, deltaker) => {
      for (const oldDeltaker of deltakere) {
        if (isDeltakerEqual(oldDeltaker, deltaker)) {
          oldDeltaker.navn = deltaker.navn
          break
        }
      }

      return deltakere
    }),
    onError: onError,
  } satisfies DeltakerMutationOptions)

  // Remove deltaker
  queryClient.setMutationDefaults(getDeltakerKeys(entity).removeBase(), {
    mutationFn: withMutationUrl(deltakerApi.delete),
    onMutate: onMutate((deltakere, deltaker) =>
      deltakere.filter((d) => !isDeltakerEqual(d, deltaker))
    ),
    onError: onError,
  } satisfies DeltakerMutationOptions)
}

setDeltakerDefaults(DeltakerTypes.VIRKSOMHET)
setDeltakerDefaults(DeltakerTypes.ANDRE)
setDeltakerDefaults(DeltakerTypes.MATTILSYNET)
